import { Box, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import PrinterFilamentsDialog from './PrinterFilamentsDialog';

function hexLuminance(hex) {
    const raw = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
        return 1;
    }
    const r = parseInt(raw.slice(0, 2), 16) / 255;
    const g = parseInt(raw.slice(2, 4), 16) / 255;
    const b = parseInt(raw.slice(4, 6), 16) / 255;
    return 0.299 * r + 0.587 * g + 0.114 * b;
}

function contrastText(hex) {
    return hexLuminance(hex) > 0.55 ? '#111111' : '#FFFFFF';
}

function MarqueeText({ text, color }) {
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const [overflow, setOverflow] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        const label = textRef.current;
        if (!container || !label) {
            return undefined;
        }
        const measure = () => {
            setOverflow(label.scrollWidth > container.clientWidth + 1);
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(measure)
            : null;
        observer?.observe(container);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [text]);

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                overflow: 'hidden',
                px: 0.4,
            }}
        >
            <Typography
                ref={textRef}
                component="span"
                sx={{
                    display: 'inline-block',
                    whiteSpace: 'nowrap',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    color,
                    ...(overflow ? {
                        animation: 'printerFilamentMarquee 4.5s linear infinite',
                        '@keyframes printerFilamentMarquee': {
                            '0%, 12%': { transform: 'translateX(0)' },
                            '88%, 100%': { transform: 'translateX(calc(-100% + 28px))' },
                        },
                    } : {}),
                }}
            >
                {text}
            </Typography>
        </Box>
    );
}

function stopNestedCardClick(event) {
    event?.stopPropagation?.();
    event?.preventDefault?.();
}

function FilamentCircle({ assignment, empty, onClick }) {
    const bg = empty ? 'transparent' : (assignment?.color || '#999999');
    const fg = empty ? 'text.disabled' : contrastText(bg);
    return (
        <Box
            component="button"
            type="button"
            onMouseDown={stopNestedCardClick}
            onClick={(event) => {
                stopNestedCardClick(event);
                onClick?.(event);
            }}
            title={empty
                ? 'Sem filamento vinculado'
                : `${assignment?.location || ''} · ${assignment?.type || ''}`.trim()}
            sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: empty ? '2px dashed' : '1px solid',
                borderColor: empty ? 'divider' : 'rgba(0,0,0,0.18)',
                bgcolor: bg,
                color: fg,
                p: 0,
                m: 0,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
                '&:hover': {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 1,
                },
            }}
        >
            {!empty && (
                <MarqueeText text={assignment?.type || '?'} color={fg} />
            )}
        </Box>
    );
}

function filamentsSignature(list) {
    if (!Array.isArray(list) || list.length === 0) {
        return '';
    }
    return list
        .map((item) => `${item.uuid || item.filament_uuid}:${item.location || ''}:${item.sort_order || 0}`)
        .join('|');
}

/**
 * Circles for filaments loaded on a printer. Click opens the manage modal.
 * @param {object} props
 * @param {string} props.printerUuid
 * @param {string} [props.printerName]
 * @param {Array} [props.filaments]
 * @param {function} [props.onChanged]
 * @param {boolean} [props.stopCardClick]
 */
export default function PrinterFilamentSlots({
    printerUuid,
    printerName,
    filaments,
    onChanged,
    stopCardClick = true,
    restriction = null,
}) {
    const propsItems = Array.isArray(filaments) ? filaments : [];
    const propsSignature = filamentsSignature(propsItems);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(propsItems);

    // Sync from parent only when printer or assignment content changes — not on
    // every render with a fresh `[]` default, which would wipe optimistic updates.
    useEffect(() => {
        setItems(Array.isArray(filaments) ? filaments : []);
    }, [printerUuid, propsSignature, filaments]);

    const handleOpen = (event) => {
        if (stopCardClick) {
            stopNestedCardClick(event);
        }
        if (!printerUuid) {
            return;
        }
        setOpen(true);
    };

    return (
        <>
            <Box
                data-printer-filament-slots
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}
                onMouseDown={(event) => stopCardClick && stopNestedCardClick(event)}
                onClick={(event) => stopCardClick && stopNestedCardClick(event)}
            >
                {items.length === 0 ? (
                    <FilamentCircle empty onClick={handleOpen} />
                ) : (
                    items.map((assignment) => (
                        <FilamentCircle
                            key={assignment.uuid || `${assignment.filament_uuid}-${assignment.sort_order}`}
                            assignment={assignment}
                            onClick={handleOpen}
                        />
                    ))
                )}
            </Box>
            {open && (
                <PrinterFilamentsDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    printerUuid={printerUuid}
                    printerName={printerName}
                    initialFilaments={items}
                    restriction={restriction}
                    onSaved={(next) => {
                        const saved = Array.isArray(next) ? next : [];
                        setItems(saved);
                        onChanged?.(saved);
                        setOpen(false);
                    }}
                />
            )}
        </>
    );
}
